import {
  Box,
  IconButton,
  Slide,
  SlideProps,
  Snackbar,
  SxProps,
  Theme,
  paperClasses,
  snackbarContentClasses,
} from '@mui/material'
import Bluebird from 'bluebird'
import { isNil } from 'lodash'
import { ReactNode, createContext, useContext, useState } from 'react'
import { FiExternalLink } from 'react-icons/fi'
import { RxCheckCircled, RxCross2, RxCrossCircled, RxInfoCircled } from 'react-icons/rx'
import useStyles from '~/common/hooks/useStyles'
import constants from '../../common/config/config'
import { useTypedTranslation } from '../../common/hooks/useTypedTranslation'
import Link from './Link'

export enum SnackbarType {
  SUCCESS = 'success',
  ERROR = 'error',
  INFO = 'info',
  // For use in push notifications
  PUSH = 'push',
}

function getStyles(theme: Theme) {
  return {
    snackbar(severity: SnackbarType) {
      let backgroundColor = ''
      let border = null
      let color = theme.palette.common.white
      let innerShadow = null
      switch (severity) {
        case SnackbarType.SUCCESS:
          backgroundColor = theme.palette.success.main
          break
        case SnackbarType.ERROR:
          backgroundColor = theme.palette.error.main
          break
        case SnackbarType.INFO:
          backgroundColor = theme.palette.common.doss.gray[11]
          border = `1px solid ${theme.palette.common.doss.gray[10]}`
          color = theme.palette.common.doss.gray[2]
          innerShadow = `inset 0 0px 4px ${theme.palette.common.doss.gray[10]}`
          break
        case SnackbarType.PUSH:
          backgroundColor = theme.palette.common.doss.white
          border = `1px solid ${theme.palette.common.doss.gray[6]}`
          break
      }
      return {
        [`& .${snackbarContentClasses.root}`]: {
          borderRadius: '2px',
          border,
          backgroundColor,
          marginTop: '65px',
          color,
          boxShadow: innerShadow ?? 'none',
        },
        [`& .${paperClasses.root}`]: {
          display: 'flex',
          alignItems: 'center',
        },
      }
    },
    content: {
      display: 'flex',
      gap: theme.spacing(1),
      alignItems: 'center',
      justifyContent: 'center',
      maxWidth: '350px',
      icon: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '24px',
        height: '24px',
      },
    },
    icon(severity: SnackbarType) {
      let color = ''
      switch (severity) {
        case SnackbarType.SUCCESS:
        case SnackbarType.ERROR:
        case SnackbarType.INFO:
          color = theme.palette.common.white
          break
        case SnackbarType.PUSH:
          color = theme.palette.common.black
          break
      }
      return {
        fontSize: '20px',
        color,
        marginInlineEnd: theme.spacing(1),
      }
    },
  }
}

const SnackbarContext = createContext({
  show: (_args: {
    jsxMessage: JSX.Element
    severity: SnackbarType
    duration: number
    link?: string
    icon?: JSX.Element
  }): void => undefined,
  showLoading: (
    _message?: string | JSX.Element,
    _link?: string,
    _styles?: SxProps<Theme>,
    _duration?: number,
    _icon?: JSX.Element,
  ): void => undefined,
  showError: (
    _error: string | JSX.Element,
    _link?: string,
    _styles?: SxProps<Theme>,
    _duration?: number,
  ): void => undefined,
  showSuccess: (
    _message?: string | JSX.Element,
    _link?: string,
    _styles?: SxProps<Theme>,
    _duration?: number,
  ): void => undefined,
  showInfo: (
    _message: string | JSX.Element,
    _link?: string,
    _styles?: SxProps<Theme>,
    _duration?: number,
  ): void => undefined,
})

export type DossSnackbarProviderProps = {
  children: ReactNode
}

/**
 * Inspired from https://github.com/TeamWertarbyte/material-ui-snackbar-provider
 * This is a provider that allows us to show snackbars from anywhere in the app
 */
export function DossSnackbarProvider({ children }: DossSnackbarProviderProps) {
  const [jsxMessage, setJsxMessage] = useState<JSX.Element>(<></>)
  const [open, setOpen] = useState(false)
  const [duration, setDuration] = useState(0)
  const [severity, setSeverity] = useState<SnackbarType>(SnackbarType.SUCCESS)
  const [linkValue, setLinkValue] = useState<string | undefined>()
  const [icon, setIcon] = useState<JSX.Element | undefined>()
  const { t } = useTypedTranslation()

  const contextValue = {
    show: (args: {
      jsxMessage: JSX.Element
      severity: SnackbarType
      duration: number
      link?: string
      icon?: JSX.Element
    }) => {
      setJsxMessage(args.jsxMessage)
      setSeverity(args.severity)
      setDuration(args.duration)
      setLinkValue(args.link)
      setIcon(args.icon)
      setOpen(true)
    },
    showLoading: (
      message?: string | JSX.Element,
      link?: string,
      styles?: SxProps<Theme>,
      duration?: number,
      icon?: JSX.Element,
    ) => {
      contextValue.show({
        jsxMessage: <Box sx={styles}>{message ?? t('snackbar.loading')}</Box>,
        severity: SnackbarType.INFO,
        duration: duration ?? 5000,
        icon: icon ?? (link ? <FiExternalLink /> : <RxInfoCircled />),
        link,
      })
    },
    showError: (
      error: string | JSX.Element,
      link?: string,
      styles?: SxProps<Theme>,
      duration?: number,
    ) => {
      const suppressError = constants.isProduction && error.toString().includes('prisma')
      contextValue.show({
        jsxMessage: (
          <Box sx={styles}>{suppressError ? t('snackbar.error_exporting_table') : error}</Box>
        ),
        severity: SnackbarType.ERROR,
        duration: duration ?? 20000,
        icon: link ? <FiExternalLink /> : <RxCrossCircled />,
        link,
      })
    },
    showSuccess: (
      message?: string | JSX.Element,
      link?: string,
      styles?: SxProps<Theme>,
      duration?: number,
    ) => {
      contextValue.show({
        jsxMessage: <Box sx={styles}>{message ? message : t('snackbar.success')}</Box>,
        severity: SnackbarType.SUCCESS,
        duration: duration ?? 5000,
        icon: link ? <FiExternalLink /> : <RxCheckCircled />,
        link,
      })
    },
    showInfo: (
      message: string | JSX.Element,
      link?: string,
      styles?: SxProps<Theme>,
      duration?: number,
    ) => {
      contextValue.show({
        jsxMessage: <Box sx={styles}>{message}</Box>,
        severity: SnackbarType.INFO,
        duration: duration ?? 5000,
        icon: link ? <FiExternalLink /> : <RxInfoCircled />,
        link,
      })
    },
  }

  return (
    <>
      <SnackbarContext.Provider value={contextValue}>{children}</SnackbarContext.Provider>
      <DossSnackbar
        className="!bottom-[58px] sm:!bottom-[74px]"
        duration={duration}
        horizontal="right"
        icon={icon}
        jsxMessage={jsxMessage}
        link={linkValue}
        onClose={() => {
          setOpen(false)
        }}
        open={open}
        severity={severity}
        vertical="bottom"
      />
    </>
  )
}

interface DossSnackbarProps {
  open: boolean
  onClose: () => void
  icon?: JSX.Element
  jsxMessage: JSX.Element
  severity: SnackbarType
  duration: number
  link?: string
  vertical: 'bottom' | 'top'
  horizontal: 'left' | 'right' | 'center'
  className?: string
}

/**
 * This is a wrapper around MUI's Snackbar component that allows us to pass in a JSX element as the
 * message along with the close button and transition styling
 */
export function DossSnackbar({
  open,
  onClose,
  icon,
  jsxMessage,
  severity,
  duration,
  link,
  vertical,
  horizontal = 'right',
  className,
}: DossSnackbarProps) {
  const styles = useStyles(getStyles)
  if (!open) {
    return null
  }

  // handleClickaway with a delay on the snackbar close to prevent it from disappearing immediately
  const handleClickaway = () => {
    Bluebird.delay(300).then(() => {
      onClose()
    })
  }

  const snackbarValue = (
    <Box sx={styles.content}>
      {isNil(icon) ? null : <Box sx={styles.content.icon}>{icon}</Box>}
      {jsxMessage}
    </Box>
  )
  return (
    <Snackbar
      ClickAwayListenerProps={{ mouseEvent: false }}
      TransitionComponent={(props: SlideProps) => <Slide {...props} />}
      action={
        <IconButton color="inherit" onClick={onClose} size="small" sx={styles.icon(severity)}>
          <RxCross2 fontSize="16px" />
        </IconButton>
      }
      anchorOrigin={{ vertical, horizontal }}
      autoHideDuration={duration}
      className={className}
      message={
        <Box sx={styles.content}>
          {link ? (
            <Link
              color="white"
              href={link}
              onClick={handleClickaway}
              rel="noopener noreferrer"
              target="_blank"
              underline="hover"
            >
              {snackbarValue}
            </Link>
          ) : (
            snackbarValue
          )}
        </Box>
      }
      onClose={onClose}
      open={open}
      sx={styles.snackbar(severity)}
    />
  )
}

/**
 * Hook to use the Doss snackbar
 */
export function useDossSnackbar() {
  return useContext(SnackbarContext)
}
